<script lang="ts">
  import { ClosingCta, type ClosingCtaProps } from '@squareup/public-web-design-system';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';
  import type { ClosingCtaModuleProps } from './types';

  const {
    entryId,
    customCssClass,
    colorMode,
    variant,
    // verticalAlignment only works if the variant is left because it is aligning Section's heading and ctaItems and
    // that is the only variant where they are on the same horizontal line
    verticalAlignment,
    heading,
    headingLevel,
    headingSize,
    ctaItems,
    content,
  }: ClosingCtaModuleProps = $props();

  const closingCtaModuleProps: ClosingCtaProps = {
    class: `ClosingCtaModule ${customCssClass}`,
    'data-entry-id': entryId,
    colorMode,
    variant,
    verticalAlignment,
    headingText: heading,
    headingLevel,
    headingSize,
  };
</script>

{#snippet closingCtaModuleCtaItems()}
  <DynamicChildrenComponent children={ctaItems} />
{/snippet}

{#snippet closingCtaModuleChildren()}
  <DynamicChildrenComponent children={content} />
{/snippet}

{#if ctaItems?.length && content?.length}
  <ClosingCta {...closingCtaModuleProps}>
    {#snippet ctaItems()}
      {@render closingCtaModuleCtaItems()}
    {/snippet}
    {@render closingCtaModuleChildren()}
  </ClosingCta>
{:else if ctaItems?.length}
  <ClosingCta {...closingCtaModuleProps}>
    {#snippet ctaItems()}
      {@render closingCtaModuleCtaItems()}
    {/snippet}
  </ClosingCta>
{:else if content?.length}
  <ClosingCta {...closingCtaModuleProps}>
    {@render closingCtaModuleChildren()}
  </ClosingCta>
{:else}
  <ClosingCta {...closingCtaModuleProps} />
{/if}

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/tokens/generated-tokens/scss/_media_queries' as media;

  @layer standard-components {
    :global {
      .ClosingCtaModule {
        .LeadFormSection {
          text-align: left;

          // Ignore Contentful settings because we always want the form in ClosingCtaModule to be left aligned
          .TextStack {
            --text-stack-cta-gap: var(--space-30) !important;
            // Even though .ClosingCtaModule__form is using passThroughProps the introTextAlignment prop is not
            // passed to the FollowUpLeadForm, so we still need to add the styling overrides here
            --text-stack-flex-alignment: flex-start !important;
            --text-stack-text-alignment: left !important;
            --text-stack-optical-text-indent: 0.1ch !important;
          }

          // LeadForm section sets a width on content, so overriding it to make sure the form doesn't overflow
          .content,
          // FollowUpLeadFormComponent sets a width on content-width-large
          .FollowUpLeadForm__content,
          // LeadForm sets a width on singleFormField. This can be seen in a form with 1 field like Passive General
          // Marketo form that only captures an email address
          .singleFieldForm,
          // LeadForm sets a width on row-with-1-columns. This can be seen in a form where you have to acknowledge you
          // agree to the terms like the Master Passive Follow Up Section
          .row-with-1-columns > * {
            width: auto !important;
          }

          // The LeadFormSection only takes up half of the window, so having 2 columns on medium size screens is not
          // leaving enough room for the fields
          .row-with-2-columns {
            // Row styling uses a max-width media query, so also using max-width here in case they ever change from flex
            // to grid
            @include media.until('large') {
              display: block;
            }
          }
        }
      }
    }
  }
</style>
